import { useData } from 'vike-react/useData';
import { Building2, ShieldCheck, Users } from 'lucide-react';
import { getAudienceById } from '@shared/constants/audiences';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { SectorHero } from '../../components/sector/SectorHero';
import { SectorProof } from '../../components/sector/SectorProof';
import { SectorFAQ } from '../../components/sector/SectorFAQ';
import { SectorCTA } from '../../components/sector/SectorCTA';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import type { Data } from './+data';

const AUDIENCE_ID = 'collectivites' as const;

const CONFORMITE = [
  { label: 'Décret Tertiaire', sub: 'Déclaration OPERAT (Décret Tertiaire) — 30 sept · -40 % à 2030' },
  { label: 'Décret BACS 2026', sub: 'Bâtiments publics > 290 kW (obligation BACS — pilotage automatique)' },
  { label: 'Code commande publique', sub: 'CCAG-PI · CPV 71314300' },
  { label: 'Charte qualité officielle des primes énergie (CEE)', sub: 'Décret 25 mars 2022' },
];

export default function CollectivitesPage() {
  const { journey, tools, cases } = useData<Data>();
  const audience = getAudienceById(AUDIENCE_ID)!;
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Collectivités', url: 'https://solutionecofrance.fr/collectivites' },
        ]}
      />

      <SectorHero
        audience={audience}
        voice={voice}
        promiseList={[
          { label: 'Fonds vert · DETR (dotation équipement) · DSIL (dotation soutien investissement local)', value: 'cumulés', tone: 'brand' },
          { label: 'Primes énergie (CEE) patrimoine', value: 'mobilisées', tone: 'accent' },
          { label: 'Trajectoire Décret Tertiaire (OPERAT)', value: '-40 % 2030', tone: 'ink' },
          { label: 'Pré-étude patrimoine', value: 'offerte', tone: 'brand' },
        ]}
      />

      {/* CONFORMITÉ BAR — référentiel réglementaire */}
      <section className="border-y border-stone-200/60 bg-stone-50/50 py-8">
        <Container>
          <p className="mb-5 text-center text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
            Cadre réglementaire couvert
          </p>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-stone-200/50 sm:grid-cols-4">
            {CONFORMITE.map((c) => (
              <div key={c.label} className="bg-paper px-4 py-3 text-center">
                <p className="font-display text-sm font-semibold text-ink">{c.label}</p>
                <p className="mt-0.5 text-[11px] text-stone-500">{c.sub}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <SectorProof audience={audience} voice={voice} variant="bento" />

      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      {/* TRUST / NDA — spécifique collectivités */}
      <section className="bg-stone-50/50 py-16">
        <Container className="max-w-4xl">
          <Reveal>
            <div className="rounded-2xl border border-stone-200/70 bg-paper p-7 md:p-10">
              <div className="grid gap-6 md:grid-cols-3 md:items-center">
                <div className="md:col-span-2">
                  <p className="eyebrow">Engagements</p>
                  <h3 className="mt-2 h-section">
                    Confidentialité, neutralité, déontologie achat public.
                  </h3>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                    Accord de confidentialité (NDA) dès le premier RDV. Aucune attribution exclusive. Conformité au code de
                    la commande publique. Mise à disposition d&rsquo;un référent commercial dédié.
                  </p>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Accord de confidentialité (NDA) disponible', icon: ShieldCheck },
                    { label: 'Référent dédié', icon: Users },
                    { label: 'CCAG-PI compatible', icon: Building2 },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.label}
                        className="flex items-center gap-2.5 rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
                      >
                        <Icon
                          className="h-3.5 w-3.5 shrink-0 text-lavender-700"
                          strokeWidth={1.7}
                        />
                        <span className="text-sm font-medium text-stone-700">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} />
    </>
  );
}
